package com.partejar.app.feature.calendar.presentation

import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.feature.calendar.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.koin.compose.koinInject

class CalendarSyncController(
    private val integration: CalendarIntegration,
    private val preferences: CalendarSyncPreferences
) {
    private val sync = DeviceCalendarSync(integration, preferences)
    private var pending: Pair<String, CalendarEventDraft>? = null
    var suggesting by mutableStateOf(false)
        private set
    var busy by mutableStateOf(false)
        private set
    var feedback by mutableStateOf<String?>(null)
        private set
    var needsPermission by mutableStateOf(false)
        private set

    suspend fun submit(key: String, draft: CalendarEventDraft, suggestIfDisabled: Boolean = true, reportSuccess: Boolean = true, forceSuggestion: Boolean = false) {
        if (!preferences.enabled) {
            if (suggestIfDisabled && (forceSuggestion || !preferences.suggestionShown)) {
                if (integration.getPermissionStatus() == CalendarPermissionStatus.UNAVAILABLE) {
                    feedback = "A integração com a agenda do celular não está disponível nesta plataforma."
                } else {
                    pending = key to draft
                    preferences.suggestionShown = true
                    suggesting = true
                }
            }
            return
        }
        pending = key to draft
        savePending(reportSuccess)
    }

    fun decline() { suggesting = false; pending = null }
    fun dismissFeedback() { feedback = null; pending = null; needsPermission = false }

    suspend fun allow() {
        if (busy) return
        busy = true
        suggesting = false
        try {
            val status = integration.requestPermission()
            preferences.enabled = status == CalendarPermissionStatus.GRANTED
            if (preferences.enabled) {
                savePending()
            } else {
                needsPermission = true
                feedback = "O compromisso foi salvo no Partejar. O acesso à agenda não foi concedido; você pode ativá-lo nas configurações."
            }
        } catch (error: CancellationException) { throw error }
        catch (_: Exception) { feedback = "O compromisso foi salvo no Partejar, mas não foi possível solicitar acesso à agenda." }
        finally { busy = false }
    }

    suspend fun retry() {
        if (busy) return
        if (needsPermission && integration.getPermissionStatus() != CalendarPermissionStatus.GRANTED) {
            integration.openSystemSettings()
            return
        }
        if (needsPermission) preferences.enabled = true
        savePending()
    }

    private suspend fun savePending(reportSuccess: Boolean = true) {
        val (key, draft) = pending ?: return
        busy = true
        try {
            val result = sync.save(key, draft)
            needsPermission = result.status == CalendarEventSaveStatus.PERMISSION_DENIED
            feedback = when (result.status) {
                CalendarEventSaveStatus.SAVED -> if (reportSuccess) "Compromisso sincronizado com a agenda do celular." else null
                CalendarEventSaveStatus.PERMISSION_DENIED -> "O compromisso está salvo no Partejar. Permita o acesso ao calendário nos ajustes do celular e tente sincronizar novamente."
                CalendarEventSaveStatus.UNAVAILABLE -> "O compromisso está salvo no Partejar. Configure uma agenda que permita gravar eventos no celular e tente sincronizar novamente."
                CalendarEventSaveStatus.FAILED -> "O compromisso está salvo no Partejar, mas não foi possível sincronizá-lo com a agenda do celular. Tente novamente."
            }
            if (result.status == CalendarEventSaveStatus.SAVED) pending = null
        } catch (error: CancellationException) { throw error }
        catch (_: Exception) { feedback = "O compromisso está salvo no Partejar, mas não foi possível sincronizá-lo com a agenda do celular." }
        finally { busy = false }
    }

    val canRetry get() = pending != null
}

@Composable
internal fun rememberCalendarSyncController(): CalendarSyncController {
    val integration = rememberCalendarIntegration()
    val preferences: CalendarSyncPreferences = koinInject()
    return remember(integration, preferences) { CalendarSyncController(integration, preferences) }
}

@Composable
internal fun DeviceCalendarSyncDialogs(controller: CalendarSyncController) {
    val scope = rememberCoroutineScope()
    if (controller.suggesting) {
        AlertDialog(
            onDismissRequest = controller::decline,
            title = { Text("Integrar com a agenda do celular?") },
            text = { Text("Permita o acesso ao calendário para adicionar suas reuniões e eventos automaticamente. Quando você editar um compromisso no Partejar, o horário na agenda do celular também será atualizado.") },
            confirmButton = { PartejarButton("Permitir acesso", { scope.launch { controller.allow() } }, isLoading = controller.busy) },
            dismissButton = { PartejarButton("Agora não", controller::decline, variant = PartejarButtonVariant.TERTIARY) }
        )
    }
    controller.feedback?.let { message ->
        AlertDialog(
            onDismissRequest = { if (!controller.busy) controller.dismissFeedback() },
            title = { Text("Agenda do celular") },
            text = { Text(message) },
            confirmButton = {
                if (controller.canRetry) PartejarButton(if (controller.needsPermission) "Verificar acesso" else "Tentar novamente",
                    { scope.launch { controller.retry() } }, isLoading = controller.busy)
                else PartejarButton("Concluir", controller::dismissFeedback)
            },
            dismissButton = {
                if (controller.canRetry) PartejarButton("Fechar", controller::dismissFeedback,
                    enabled = !controller.busy, variant = PartejarButtonVariant.TERTIARY)
            }
        )
    }
}
